import { toast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import { useLicenseSeatRevokeMutation } from "@/v2-backoffice-client/license/seats/license.seats.revoke.mutation"
import { evictLicenseSeatQueries } from "@/v2-backoffice-client/license/license.utils"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { LicensePlanSeatHolderRow } from "@/v2-backoffice-client/license/plan/view/LicensePlanSeatHolderTable"

export type LicensePlanSeatHolderRowToolbarProps = {
  holder: LicensePlanSeatHolderRow
}

// The one action a named seat supports from the plan side: revoking it —
// the same write as the switch in the customer's Seats dialog, offered here
// because a stale seat is found on this list (every customer at once) and
// should not need a detour to the customer panel to be removed. The person
// stays a user; only the seat row goes.
function LicensePlanSeatHolderRowToolbar(
  props: LicensePlanSeatHolderRowToolbarProps,
) {
  const { holder } = props
  const personName = formatUserName(
    holder.User?.FirstName,
    holder.User?.LastName,
  )
  const customerName = holder.CustomerName ?? "the customer"

  const [revokeSeat, { loading, error }] = useLicenseSeatRevokeMutation({
    update: evictLicenseSeatQueries,
    onCompleted() {
      toast({
        variant: "success",
        title: "Seat revoked",
        description: `${personName} is no longer named on ${customerName}'s license.`,
      })
    },
  })

  return (
    <ActionMenu>
      <ActionMenu.Trigger />

      <ActionMenu.Content>
        <ActionMenu.Label>Seat Actions</ActionMenu.Label>

        <ActionMenu.ConfirmItem
          iconSrc="trash"
          variant="destructive"
          isLoading={loading}
          error={error}
          errorTitle="Failed to revoke seat"
          confirmTitle="Revoke Seat"
          confirmDescription={
            holder.IsActiveUser
              ? `${personName} loses what ${customerName}'s license gives them the moment entitlements next resolve. The license itself is untouched, and the seat can be named again in the customer's Seats dialog.`
              : `${personName} is no longer an active user of ${customerName}, so this seat grants nothing; revoking it frees the named count on the license.`
          }
          confirmText="Revoke"
          onConfirm={() => {
            void revokeSeat({
              variables: { licenseId: holder.LicenseId, userId: holder.UserId },
            })
          }}
        >
          Revoke Seat
        </ActionMenu.ConfirmItem>
      </ActionMenu.Content>
    </ActionMenu>
  )
}

export default LicensePlanSeatHolderRowToolbar
